import React, {useState, useEffect} from 'react';
import './ReportLandingPage.scss';
import {Box, Typography} from '@mui/material';
import {ReactComponent as InfoIcon} from '../../assets/images/info-icon.svg';
import {ReactComponent as DeleteIcon} from '../../assets/images/delete.svg';
import {t} from 'i18next';
import {
  useReportLanding,
  useDeleteReport,
} from '../../hooks/reporting/useReporting';
import ArrNavigationList, {
  NavigationListItem,
} from '../common/arr-navigation-list/ArrNavigationList';
import {TemplateData} from '../../api/types/reportingApi.type';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import ArrModal from '../common/arr-modal/ArrModal';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../shared/constants';
import ArrTooltipWrapper from '../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {Trans} from 'react-i18next';

interface ReportHeaderProps {
  headingWithCount?: boolean;
  bigHeader?: boolean;
  heading: string;
  count?: number;
  headingClassName?: string;
  dataTestId: string;
}

const roles = {
  reviewManager: 'Review Manager',
  projectAdmin: 'Project Administrator',
};

const ReportLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const isProjectAdmin = loginUserData?.roleName === roles.projectAdmin;

  const {data: reportLandingData, refetch: refetchReportLanding} =
    useReportLanding({
      sessionDocConceptReportID: 'sessionDocConceptReportID',
      reportMasterDoclistId: 0,
      source: 'source',
    });

  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [toastMessage, setToastMessage] = useState('');

  const {mutate: deleteReport} = useDeleteReport(
    () => {
      refetchReportLanding();
      setToastStatus([ToastType.Success]);
      setToastMessage(
        t('reports.landingPage.deleteAction.deleteSuccessMessage')
      );
    },
    (error) => {
      console.error('Delete failed:', error);
      setToastStatus([ToastType.Error]);
      setToastMessage(
        t('reports.landingPage.deleteAction.deleteFailureMessage')
      );
    }
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

  const mapTemplateDataToNavItems = (
    templates: TemplateData[] | undefined,
    canViewAction: boolean,
    customTemplate: boolean,
    actionIcon?: string
  ): NavigationListItem[] => {
    const updatedLabel = (label: string) => {
      const trimmedLabel = label.trim();
      return label === 'Custom Document Data Report'
        ? 'Data Export'
        : trimmedLabel.trimEnd().replace(/report$/i, '');
    };
    return (
      templates?.map((item) => ({
        id: item.reportID.toString(),
        label: customTemplate
          ? item.reportLabel
          : updatedLabel(item.reportLabel),
        route: item.reportUrl,
        disabled:
          isProjectAdmin &&
          (item.reportLabel === 'Advanced Batch Management Report' ||
            item.reportLabel === 'Review Progress by Assignment Report' ||
            item.reportLabel === 'Review Progress by Reviewer Report'),
        canViewAction,
        actionIcon,
        reportTypeCode: item.reportTypeCode,
        isCustomReport: item.isCustomReport,
        isShared: item.isShared,
      })) ?? []
    );
  };

  const handleNavigate = (item: NavigationListItem) => {
    if (!item?.route) return;

    const match = item.route.match(/[?&]ReportID=(\d+)/);
    const reportId = match?.[1];
    if (item.route.includes('communication')) {
      const labelMatch = item.route.match(/[?&]ReportLabel=([^&]+)/);
      const labelval = labelMatch?.[1] || '';
      navigate(
        `/DataAnalysisReport/CommunicationExplorerReport?ReportID=${reportId}&ReportLabel=${labelval}`,
        {
          state: {item, fromReportLanding: true},
        }
      );
    } else {
      const to = item.route.startsWith('/') ? item.route : `/${item.route}`;
      navigate(to, {
        state: {item, reportId, fromReportLanding: true},
      });
    }
  };

  const actionLabel = 'Delete item';

  const RenderBoxHeader: React.FC<ReportHeaderProps> = ({
    headingWithCount = false,
    bigHeader = false,
    heading,
    count = 0,
    headingClassName,
    dataTestId,
  }) => {
    return (
      <div
        className={`report-box-heading ${
          headingClassName ? headingClassName : ''
        }`}
        data-testid={`report-landing-page-div-${dataTestId}`}
      >
        <Typography
          className={`typo-text template-heading-text ${
            bigHeader ? 'subhead4_size' : ' subhead5_size'
          }`}
          aria-label={`${heading} ${headingWithCount ? `(${count})` : ''}`}
          role="header"
          tabIndex={0}
          data-testid={`report-landing-page-heading-with-count-${dataTestId}`}
        >
          {`${heading} ${headingWithCount ? `(${count})` : ''}`}
        </Typography>
      </div>
    );
  };

  const handleOpenDeleteModal = (reportId: number) => {
    setSelectedReportId(reportId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedReportId(null);
  };

  const handleConfirmDelete = () => {
    if (selectedReportId) {
      deleteReport(selectedReportId);
    }
    setShowDeleteModal(false);
  };

  const handleCloseToast = () => {
    setToastStatus([]);
    setToastMessage('');
  };

  useEffect(() => {
    if (showDeleteModal) {
      const focusTarget = document.querySelector(
        '[data-testid="modal-cancel-btn"]'
      ) as HTMLElement | null;
      if (focusTarget) {
        setTimeout(() => {
          focusTarget.focus();
        }, 0);
      }
    }
  }, [showDeleteModal]);

  const modalTitleText = t('common.delete');
  const modalDescriptionText = t(
    'reports.landingPage.deleteAction.deleteModalDescription'
  );
  const desiredAnnouncement = `${modalTitleText}\n${modalDescriptionText}`;

  return (
    <Box className="report-landing-page">
      <div className="report-heading" data-testid={`report-landing-page-title`}>
        <Typography
          className="typo-text headline3_size"
          aria-label={t('reports.landingPage.title')}
          role="header"
          tabIndex={0}
          data-testid={`report-landing-page-title-text`}
        >
          {t('reports.landingPage.title')}
        </Typography>
        <ArrTooltipWrapper
          title={t('reports.landingPage.help.title')}
          trigger={
            <InfoIcon
              fontSize="small"
              className="info-icon"
              data-testid="report-landing-title-info"
            />
          }
        >
          <Trans i18nKey="reports.landingPage.help.content" />
        </ArrTooltipWrapper>
      </div>
      <Box className="report-boxes-main">
        <div
          className="report-vertical-div"
          data-testid={`report-landing-page-div-btn`}
        >
          <RenderBoxHeader
            heading={t('reports.landingPage.dataExplorationReports')}
            bigHeader={true}
            dataTestId="data-exploration-reports"
          />
          <div
            className="template-div"
            data-testid={`report-landing-page-div-button`}
          >
            <ArrNavigationList
              items={mapTemplateDataToNavItems(
                reportLandingData?.reportModel.dataExplorationTemplate,
                false,
                false,
                ''
              )}
              onNavigate={handleNavigate}
              actionLabel={actionLabel}
              actionIcon={<></>}
              useLabelForTestId
              dataTestId="data-exploration-template-data"
            />
          </div>

          <RenderBoxHeader
            heading={t('reports.landingPage.customReports')}
            headingWithCount={true}
            headingClassName="custom-report-heading"
            count={
              reportLandingData?.reportModel.dataExplorationCustomTemplate
                .length
            }
            dataTestId="custom-reports-data-explore"
          />
          <div
            className="custom-template-div"
            data-testid={`report-landing-page-div-icon`}
          >
            <ArrNavigationList
              items={mapTemplateDataToNavItems(
                reportLandingData?.reportModel.dataExplorationCustomTemplate,
                true,
                true,
                'delete'
              )}
              onNavigate={handleNavigate}
              onAction={(itemId) => handleOpenDeleteModal(Number(itemId))}
              actionLabel={actionLabel}
              actionIcon={<DeleteIcon className="delete-icon" />}
              useLabelForTestId
              dataTestId="data-exploration-custom"
            />
          </div>
        </div>

        <div
          className="report-vertical-div"
          data-testid={`report-landing-page-div-link`}
        >
          <RenderBoxHeader
            heading={t('reports.landingPage.reviewReports')}
            bigHeader={true}
            dataTestId="review-reports"
          />
          <div
            className="template-div"
            data-testid={`report-landing-page-div-input`}
          >
            <ArrNavigationList
              items={mapTemplateDataToNavItems(
                reportLandingData?.reportModel.reviewTemplate,
                false,
                false,
                ''
              )}
              onNavigate={handleNavigate}
              actionIcon={<></>}
              actionLabel={actionLabel}
              useLabelForTestId
              dataTestId="review-reports-template"
            />
          </div>

          <RenderBoxHeader
            heading={t('reports.landingPage.customReports')}
            headingWithCount={true}
            headingClassName="custom-report-heading"
            count={reportLandingData?.reportModel.reviewCustomTemplate.length}
            dataTestId="custom-reports-review-reports"
          />
          <div
            className="custom-template-div"
            data-testid={`report-landing-page-div-text`}
          >
            <ArrNavigationList
              items={mapTemplateDataToNavItems(
                reportLandingData?.reportModel.reviewCustomTemplate,
                true,
                true,
                'delete'
              )}
              onNavigate={handleNavigate}
              onAction={(itemId) => handleOpenDeleteModal(Number(itemId))}
              actionLabel={actionLabel}
              actionIcon={<DeleteIcon className="delete-icon" />}
              useLabelForTestId
              dataTestId="review-custom-template"
            />
          </div>
        </div>

        <div
          className="report-vertical-div"
          data-testid={`report-landing-page-div-container`}
        >
          <RenderBoxHeader
            heading={t('reports.landingPage.otherReports')}
            bigHeader={true}
            dataTestId="other-reports-template"
          />
          <div
            className="template-div"
            data-testid={`report-landing-page-div-2`}
          >
            <ArrNavigationList
              items={mapTemplateDataToNavItems(
                reportLandingData?.reportModel.otherTemplate,
                false,
                false,
                ''
              )}
              onNavigate={handleNavigate}
              actionLabel={actionLabel}
              actionIcon={<></>}
              useLabelForTestId
              dataTestId="other-reports-template"
            />
          </div>

          <RenderBoxHeader
            heading={t('reports.landingPage.customReports')}
            headingWithCount={true}
            headingClassName="custom-report-heading"
            count={reportLandingData?.reportModel.otherCustomTemplate.length}
            dataTestId="landing-custom-reports-template"
          />
          <div
            className="custom-template-div"
            data-testid={`report-landing-page-div-3`}
          >
            <ArrNavigationList
              items={mapTemplateDataToNavItems(
                reportLandingData?.reportModel.otherCustomTemplate,
                true,
                true,
                'delete'
              )}
              onNavigate={handleNavigate}
              onAction={(itemId) => handleOpenDeleteModal(Number(itemId))}
              actionLabel={actionLabel}
              actionIcon={<DeleteIcon className="delete-icon" />}
              useLabelForTestId
              dataTestId="other-reports-custom-template"
            />
          </div>
        </div>
      </Box>
      {toastStatus.length > 0 && (
        <ArrToastNotification
          message={toastMessage}
          onClose={handleCloseToast}
          status={toastStatus}
        />
      )}
      {/* Accessibility-only live region to announce desired text when modal opens */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        role="alert"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
        }}
        data-testid={`report-landing-page-div-4`}
      >
        {showDeleteModal ? desiredAnnouncement : ''}
      </div>
      <ArrModal
        showModal={showDeleteModal}
        onClose={handleCloseDeleteModal}
        title={modalTitleText}
        description={modalDescriptionText}
        dataTestId="show-delete-modal"
        buttons={[
          {
            id: 'cancel',
            label: `${t('common.cancel')}`,
            variant: 'secondary',
            onClick: handleCloseDeleteModal,
            dataTestId: 'modal-cancel-btn',
          },
          {
            id: 'delete',
            label: `${t('common.delete')}`,
            variant: 'danger',
            onClick: handleConfirmDelete,
            dataTestId: 'modal-delete-btn',
          },
        ]}
        ariaLabel={desiredAnnouncement}
        ariaDescription={modalDescriptionText}
        hideDefaultActions={false}
        showClose={false}
      />
    </Box>
  );
};

export default ReportLandingPage;
